import * as React from "react";

/**
 * NFR-32 (WCAG 2.1 §1.3.1, §3.3.1, §4.1.2) — the link between a `<Field>`'s
 * label, hint and error and the control inside it.
 *
 * `Field` provides this context; the form controls (`Input`, `Textarea`,
 * `SelectTrigger`, `RadioGroup`) read it through `useFieldControl`. Doing it
 * here rather than by cloning `Field`'s child is deliberate: the control is
 * often not the child — it sits inside a `<Select>` root (which renders no DOM)
 * or a react-hook-form `<Controller render=…>`, where a clone cannot reach it.
 *
 * Only ONE control per field is linked: the first to mount claims the field.
 * A field that wraps several controls, each with its own label, keeps those
 * labels — the field's label is not forced onto all of them.
 */
export type FieldControlContext = {
  /** id of the rendered `<label>`, when the field has one. */
  labelId?: string;
  /** ids of the hint or error text currently rendered, space-separated. */
  describedBy?: string;
  invalid: boolean;
  required: boolean;
  /** the id of the control that claimed the field, once one has. */
  claimed: string | null;
  claim: (id: string) => () => void;
};

export const FieldContext = React.createContext<FieldControlContext | null>(null);

type AriaProps = {
  id?: string;
  "aria-describedby"?: string;
  "aria-invalid"?: React.AriaAttributes["aria-invalid"];
  "aria-required"?: React.AriaAttributes["aria-required"];
  "aria-label"?: string;
  "aria-labelledby"?: string;
};

/**
 * Returns `props` with the field's id and aria-* filled in wherever the caller
 * did not set them itself. `labelledBy` is for controls a `<label for>` does
 * not name reliably (a Radix Select trigger, a radiogroup): they are named
 * through aria-labelledby instead.
 */
export function useFieldControl<P extends AriaProps>(props: P, { labelledBy = false } = {}): P {
  const field = React.useContext(FieldContext);
  const ownId = React.useId();
  const id = props.id ?? ownId;
  const claim = field?.claim;

  React.useLayoutEffect(() => (claim ? claim(id) : undefined), [claim, id]);

  if (!field || field.claimed !== id) return props;

  const describedBy = [props["aria-describedby"], field.describedBy].filter(Boolean).join(" ") || undefined;
  const named = props["aria-label"] !== undefined || props["aria-labelledby"] !== undefined;
  return {
    ...props,
    id,
    "aria-describedby": describedBy,
    "aria-invalid": props["aria-invalid"] ?? (field.invalid ? true : undefined),
    "aria-required": props["aria-required"] ?? (field.required ? true : undefined),
    ...(labelledBy && !named && field.labelId ? { "aria-labelledby": field.labelId } : {}),
  };
}
